import { useCommonStore } from "@/stores";
import styles from "./index.module.scss";
import { ReactEventHandler, useEffect, useRef, useState } from "react";
import { 
  CollectSvg, 
  CollectedSvg, 
  CompareSvg, 
  ExclamationSvg 
} from "@/assets/icons";
import clsx from "clsx";
import { getOffset } from "@/utils";
import { TOffset } from "@/types";
import { 
  APICode, 
  CollectStatus, 
  TaskStatus 
} from "@/constants";
import { useTranslation } from "react-i18next";
import { SvgButton, Tooltip } from "@/components";
import { postCollect } from "@/api";
import { toast } from "react-toastify";

const Result = () => {
  const { t } = useTranslation();

  const rootEleRef = useRef<HTMLDivElement>(null);
  const resultEleRef = useRef<HTMLImageElement>(null);
  const [isCompare, setIsCompare] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [offset, setOffset] = useState<TOffset>();
  const [collectStatus, setCollectStatus] = useState<CollectStatus>(CollectStatus.UnCollect);
  const [collectLoading, setCollectLoading] = useState(false);

  const activeTemp = useCommonStore(state => state.activeTemp);
  const task = useCommonStore(state => state.task);
  const activeTempUrl = useCommonStore(state => state.getters.activeTempUrl);

  const sync = () => {    
    if (resultEleRef.current && rootEleRef.current && resultEleRef.current.complete) {
      const offset = getOffset(resultEleRef.current, rootEleRef.current);
      setOffset(offset);
      setWidth(resultEleRef.current.clientWidth);
      setHeight(resultEleRef.current.clientHeight);
    }
  }

  const handleLoad: ReactEventHandler = () => {
    setLoaded(true);
    sync();
  }

  const handle = (e: any) => {
    e.preventDefault();
    setIsCompare(true);
  }

  const handleCollect = () => {
    setCollectLoading(true);

    const finalCollectStatus = collectStatus === CollectStatus.Collected ? CollectStatus.UnCollect : CollectStatus.Collected;

    postCollect({ 
      taskIds: task!.taskId, 
      collection: finalCollectStatus
    })
      .then(res => {
        const { code, msg } = res;
        if (code === APICode.Success) {
          setCollectStatus(finalCollectStatus);

          toast(collectStatus === CollectStatus.Collected ? t("uncollect_success") : t("collect_success"));
        } else {
          console.error(new Error(msg));
        }
      })
      .catch(console.error)
      .finally(() => {
        setCollectLoading(false);
      })
  }

  useEffect(() => {
    const listener = () => {
      setIsCompare(false);
    }

    document.addEventListener("mouseup", listener);
    document.addEventListener("touchend", listener);

    return () => {
      document.removeEventListener("mouseup", listener);
      document.removeEventListener("touchend", listener);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("resize", sync);

    return () => {
      window.removeEventListener("resize", sync);
    }
  }, []);

  return (
    <div
      ref={rootEleRef}
      className={styles["result"]}
    >
      {(!task!.whetherViolations && task?.status === TaskStatus.Success) && (
        <>
          <img 
            ref={resultEleRef}
            className={styles["result_img"]}
            src={task?.pictureUrl}
            alt="" 
            onLoad={handleLoad}
          />

          {!loaded && (
            <div
              style={{
                aspectRatio: `${activeTemp?.dimension?.width} / ${activeTemp?.dimension?.height}`
              }}
              className={clsx(
                styles["skeleton"],
                "skeleton_loading",
                "a_center",
              )}
            ></div>
          )}

          {loaded && isCompare && (
            <img 
              style={{ width, height }}
              className={clsx(
                styles["source_img"],
                "a_center"
              )}
              src={activeTempUrl}
              alt="" 
            />
          )}

          {offset && (
            <>
              <button
                style={{ 
                  right: offset.right,
                  bottom: offset.bottom
                }}
                className={styles["compare_button"]}
                onMouseDown={handle}
                onTouchStart={handle}
                onContextMenu={e => e.preventDefault()}
              >
                <CompareSvg></CompareSvg>
              </button>

              <SvgButton
                style={{
                  right: offset.right,
                  top: offset.top,
                }}
                className={styles["custom_collect_button"]}
                disabled={collectLoading}
                onClick={handleCollect}
              >
                {collectStatus === CollectStatus.Collected ? (
                  <CollectedSvg></CollectedSvg>
                ) : (
                  <CollectSvg></CollectSvg>
                )}

                <Tooltip
                  className={styles["custom_tooltip"]}
                  content={collectStatus === CollectStatus.Collected ? t("uncollect_tip") : t("collect_tip")}
                  placement="bottom"
                ></Tooltip>
              </SvgButton>
            </>
          )}
        </>
      )}

      {(task!.whetherViolations || [TaskStatus.Failed, TaskStatus.Timeout, TaskStatus.FeTimeout].includes(task!.status)) && (
        <div
          className={styles["failed"]}
        >
          <div
            className={styles["info_wrap"]}
          >
            <div
              className={styles["icon_wrap"]}
            >
              <ExclamationSvg></ExclamationSvg>
            </div>
    
            <div
              className={styles["desc"]}
            >
              {t("generate_failed_desc")}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Result;